import { create } from 'zustand';
import { message } from 'antd';
import { axiosApi } from '../../../axios-api';
import { handleGenerationsLimitError } from '../../../helpers';
import { sortDesignsByCreatedAt } from '../utils/sortDesigns';
import { buildSanitizedRulePayload } from '../utils/sanitizeRuleFlow';

const initialNodesProjectState = {
  api: null,
  userRoles: [],

  projectId: null,
  project: null,
  ruleId: null,
  ruleName: '',
  ruleJson: null,
  feedId: null,
  feedTypes: null,

  rfNodes: null,
  projectLoading: false,

  designTemplates: [],
  elements: [],
  rule: { position: [150, 150], zoom: 1 },
};

const useNodesProjectStore = create((set, get) => ({
  ...initialNodesProjectState,

  reset: () => set({ ...initialNodesProjectState }),

  init: (api, userRoles, projectId, ruleId) => {
    set({ api, userRoles, projectId, ruleId });
  },

  fetchProject: async (projectId) => {
    const { api } = get();
    if (!api) return null;
    try {
      return await api.fetchProject(projectId);
    } catch (err) {
      console.error(err);
      message.error(err?.message ?? 'Failed to load project');
      return null;
    }
  },

  loadProject: async (projectIdOverride) => {
    const { fetchProject, userRoles, projectId: storeProjectId } = get();
    const pid = projectIdOverride || storeProjectId;
    try {
      set({ projectLoading: true });
      const result = await fetchProject(pid);
      if (!result) return;

      const foundOrganization = (userRoles ?? []).find(
        (role) => role.organization === result?.organization?.id,
      );

      let newProject = result;
      if (foundOrganization) {
        newProject = {
          ...result,
          organization: { ...result.organization, ...foundOrganization },
        };
      }

      set({
        project: newProject,
        ruleId: result.rule?.id,
        ruleJson: result.rule ? JSON.parse(result.rule.rule_json) : null,
        ruleName: result.rule?.name,
        feedId: result.feed.id,
      });
    } finally {
      set({ projectLoading: false });
    }
  },

  loadDesigns: async (reload = false) => {
    const { api, projectId, designTemplates } = get();
    if (!reload && designTemplates.length > 0) return designTemplates;
    try {
      const designs = await api.fetchDesigns(projectId);
      return designs;
    } catch (error) {
      message.error(error);
    }
  },

  loadTemplates: async () => {
    const { loadDesigns } = get();
    const designs = await loadDesigns(true);
    const sorted = sortDesignsByCreatedAt(designs);
    set({ designTemplates: sorted });
    return sorted;
  },

  fetchFeedTypes: async () => {
    const { api } = get();
    const data = await api.fetchFeedTypes();
    set({ feedTypes: data });
  },

  setNodesRf: (rfInstance) => set({ rfNodes: rfInstance }),

  setRuleFetched: () => set({ ruleJson: null }),

  downloadStage: async () => {
    const { rfNodes, ruleId, projectId, loadProject } = get();
    try {
      const flowState = rfNodes?.toObject();
      if (!flowState) {
        throw new Error('Flow state is not ready yet. Please try again.');
      }

      const json_data = buildSanitizedRulePayload(flowState);

      const url = !ruleId
        ? `/projects/${projectId}/rules/`
        : `/projects/${projectId}/rules/${ruleId}/`;

      await axiosApi({
        method: !ruleId ? 'POST' : 'PUT',
        url,
        data: {
          name: `${projectId}-${ruleId}`,
          rule_json: JSON.stringify(json_data),
        },
      });

      await loadProject(projectId);
      return true;
    } catch (error) {
      if (error.isAxiosError) {
        handleGenerationsLimitError(error);
      } else {
        message.error(error.message);
      }
      return false;
    }
  },

  run: async () => {
    const { projectId } = get();
    try {
      await axiosApi.get(`/run/${projectId}`);
      message.success('Started flow!');
    } catch (error) {
      message.error(error);
    }
  },

  deleteTemplate: async (id) => {
    const { api, projectId, loadDesigns } = get();
    try {
      const data = await api.deleteTemplate(projectId, id);
      await loadDesigns();
      return data;
    } catch (err) {
      message.error(err);
    }
  },

  setElements: (elements) => set({ elements }),

  setRule: (rule) => set({ rule }),

  deleteDesignTemplate: async (templateId) => {
    const { deleteTemplate, elements, downloadStage } = get();
    try {
      const success = await deleteTemplate(templateId);
      if (!success) {
        return message.error(`Error deleting template (${success})`);
      }

      if (elements) {
        const updatedElements = elements.map((element) => {
          if (element.type === 'graphic') {
            element.data.selected = element.data.selected.filter(
              (item) => item.toString() !== templateId.toString(),
            );
          }
          return element;
        });
        set({ elements: updatedElements });
        downloadStage();
      }
    } catch (error) {
      message.error(`Error deleting template (${error})`);
    }
  },
}));

export default useNodesProjectStore;
